import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import operatorService from '../../services/operatorService';
import { saveMobileState, loadMobileState, clearMobileState, loadMaterialIssueState } from '../../utils/mobileSessionStorage';
import { extractEmployeeNumber } from '../../utils/barcodeUtils';
import { usePackerWebSocket } from './MobilePackerView/usePackerWebSocket';
import { usePrintLabel } from './MobilePackerView/usePrintLabel';
import { useScaleWeight } from '../../hooks/useScaleWeight';
import { usePackerScanning } from './MobilePackerView/usePackerScanning';
import { useWorkerStatus } from '../../hooks/useWorkerStatus';
import PackerWeightEntry from './MobilePackerView/PackerWeightEntry';
import PackerScanConfirmation from './MobilePackerView/PackerScanConfirmation';
import PackerModals from './MobilePackerView/PackerModals';
import toast from 'react-hot-toast';
import {
  Printer,
  CheckCircle,
  LogOut,
  Loader2,
  X,
  Clock,
  Package,
  ChevronDown,
  ChevronUp,
  Menu,
  Camera,
} from 'lucide-react';
import { authService } from '../../services/api';
import ElapsedTimeDisplay from '../Operator/components/ElapsedTimeDisplay';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { useYieldFactor, formatYield } from '../../hooks/useYieldFactor';
import type { EmployeeInfo, SessionInfo } from '../../types';

interface StationInfo {
  station_id: number;
  station_name: string;
  station_barcode?: string;
  location_code?: string;
  printer_mac_id?: string; // Config key for printer MAC (e.g. "printer_test")
}

type PackerStep = 'weight_entry' | 'printing' | 'scan_confirm' | 'success';
type ScanStep = 'badge' | 'label';

interface MobilePackerViewProps {
  embedded?: boolean; // When true, hides mobile header/sidebar and uses Layout's page title
}

const MobilePackerView: React.FC<MobilePackerViewProps> = ({ embedded = false }) => {
  const { t, language, setLanguage } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get state from navigation, fall back to sessionStorage on refresh
  const navState = location.state as {
    station?: StationInfo;
    session?: SessionInfo;
    employee?: EmployeeInfo;
  } | null;
  const savedState = loadMobileState();
  const hasMatchingSavedSession =
    !!savedState?.session &&
    !!navState?.session?.sub_session_number &&
    savedState.session.sub_session_number === navState.session.sub_session_number;

  const [station] = useState<StationInfo | null>(navState?.station || savedState?.station as StationInfo || null);
  const [session, setSession] = useState<SessionInfo | null>(navState?.session || savedState?.session as SessionInfo || null);
  const [employee] = useState<EmployeeInfo | null>(navState?.employee || savedState?.employee as EmployeeInfo || null);

  // Real-time session status via WebSocket (for accurate pause state display)
  const { sessionStatus } = useWorkerStatus(station?.station_id, session?.sub_session_number);

  // Packer flow state
  const [step, setStep] = useState<PackerStep>('weight_entry');
  const [weight, setWeight] = useState('');
  // boxCount = packed boxes only (for progress bar display)
  // When the same packer session remounts on resize, prefer the fresher saved
  // progress over the original navigation payload.
  const [boxCount, setBoxCount] = useState(() => {
    const initialCount = hasMatchingSavedSession
      ? savedState?.boxCount
      : (navState?.session?.total_boxes ?? savedState?.boxCount);
    const parsed = typeof initialCount === 'string' ? parseInt(initialCount, 10) : initialCount;
    return typeof parsed === 'number' && !isNaN(parsed) ? parsed : 0;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isClockingOut, setIsClockingOut] = useState(false);
  const [isTakingBreak, setIsTakingBreak] = useState(false);
  const [showBadgeMenu, setShowBadgeMenu] = useState(false);
  const [showTargetReachedModal, setShowTargetReachedModal] = useState(false);
  const [stationScannedForMenu, setStationScannedForMenu] = useState(false); // Track if station was scanned for options menu
  const [showScanOutModal, setShowScanOutModal] = useState(false); // Scan out modal
  const [scanOutStep, setScanOutStep] = useState<'station' | 'badge'>('station'); // Track scan out step
  const [pendingScanOutAction, setPendingScanOutAction] = useState<'menu' | 'break' | 'clockout' | 'end_session'>('menu'); // Action after badge verification

  // Scan confirmation state
  const [scanStep, setScanStep] = useState<ScanStep>('badge');
  const [scannedBadge, setScannedBadge] = useState<string | null>(null);
  const badgeAcceptedAtRef = useRef<number>(0); // Cooldown to prevent badge scan leaking into label step
  const [manualInput, setManualInput] = useState('');
  const [showCamera, setShowCamera] = useState(false); // Camera scanner state
  const [lastPrintedBarcode, setLastPrintedBarcode] = useState<string | null>(null);
  const [subUnitsPacked, setSubUnitsPacked] = useState(() => {
    return hasMatchingSavedSession ? (savedState?.subUnitsPacked ?? 0) : 0;
  });
  const [subUnitIds, setSubUnitIds] = useState<number[]>(() => {
    return hasMatchingSavedSession ? (savedState?.subUnitIds ?? []) : [];
  });
  const [showSubUnitsCompleteModal, setShowSubUnitsCompleteModal] = useState(false);  // Modal when all sub-units packed

  // Refs for input focus (Android doesn't respect autoFocus attribute)
  const badgeScanInputRef = useRef<HTMLInputElement>(null);
  const labelScanInputRef = useRef<HTMLInputElement>(null);
  const pendingBoxCheckedRef = useRef(false);
  const isLocalClockOutRef = useRef(false); // Track if clock-out was initiated from this device
  const mountTimeRef = useRef(Date.now()); // Track when this component mounted (for ignoring stale events)
  const lastPrintTriggerRef = useRef<number>(0); // Debounce for scale print button (2s cooldown)

  // Auto-focus badge input when entering scan_confirm step with badge scanStep
  useEffect(() => {
    if (step === 'scan_confirm' && scanStep === 'badge' && badgeScanInputRef.current) {
      setTimeout(() => badgeScanInputRef.current?.focus(), 100);
    }
  }, [step, scanStep]);

  // Auto-focus label input when scanStep changes to label, and clear any stale input
  useEffect(() => {
    if (step === 'scan_confirm' && scanStep === 'label' && labelScanInputRef.current) {
      labelScanInputRef.current.value = '';
      setManualInput('');
      setTimeout(() => labelScanInputRef.current?.focus(), 100);
    }
  }, [step, scanStep]);

  // Yield factor from centralized API
  const { fetchForItem } = useYieldFactor();
  const [apiYield, setApiYield] = useState<number | null>(null);
  useEffect(() => {
    const itemNumber = session?.raw_material_item_number;
    if (itemNumber) {
      fetchForItem(itemNumber, session?.product_sku).then(setApiYield);
    }
  }, [session?.raw_material_item_number, session?.product_sku, fetchForItem]);

  // UI state
  const [isPackingInfoExpanded, setIsPackingInfoExpanded] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showSupervisorBadgePrompt, setShowSupervisorBadgePrompt] = useState(false);
  const [showSupervisorOverride, setShowSupervisorOverride] = useState(false);
  const [showMaterialIssueModal, setShowMaterialIssueModal] = useState(() => {
    const saved = loadMaterialIssueState();
    return saved?.isOpen ?? false;
  });
  const [overrideSupervisor, setOverrideSupervisor] = useState<{ id: string; name: string; employee_number: string } | null>(null);
  const [supervisorBadgeInput, setSupervisorBadgeInput] = useState('');
  const [isVerifyingSupervisor, setIsVerifyingSupervisor] = useState(false);

  // Resolved printer MAC address (actual MAC like "00:07:4D:47:BB:CD")
  const [printerMac, setPrinterMac] = useState<string | null>(null);
  const [printerIp, setPrinterIp] = useState<string | null>(null);

  // Label copies setting (from DB, falls back to env var, then default 2)
  const [numberOfCopies, setNumberOfCopies] = useState(Number(import.meta.env.VITE_LABEL_PRINT_COPIES) || 2);
  // Post-print scan toggle (badge + label double-scan after printing)
  const [requirePostPrintScan, setRequirePostPrintScan] = useState(true);
  const [simulatePrinter, setSimulatePrinter] = useState(false);
  // Global default weight mode from config (used when session has no override)
  const [defaultWeightMode, setDefaultWeightMode] = useState<'fixed' | 'catch' | 'scale'>('fixed');
  // Weight tolerance from config (lbs above unit weight)
  const [weightTolerance, setWeightTolerance] = useState(0.05);
  // Auto-tare settings from config
  const [bagTareEnabled, setBagTareEnabled] = useState(false);
  const [bagTareWeight, setBagTareWeight] = useState(0.05);
  const [caseTareEnabled, setCaseTareEnabled] = useState(false);
  const [caseTareWeight, setCaseTareWeight] = useState(1.35);
  // WCO-3325: configured facility location for label company info (defaults to dormant HTE1 → HTE).
  const [labelProductionLocation, setLabelProductionLocation] = useState('HTE');
  usePackerWebSocket({
    session, setSession, employee, station,
    isLocalClockOutRef, mountTimeRef,
    setStep, setWeight, setLastPrintedBarcode, setScanStep, setSubUnitsPacked,
    setBoxCount,
    setNumberOfCopies, setPrinterMac, setPrinterIp, setRequirePostPrintScan,
    setSimulatePrinter, setDefaultWeightMode, setWeightTolerance,
    setBagTareEnabled, setBagTareWeight, setCaseTareEnabled, setCaseTareWeight,
    setLabelProductionLocation,
  });

  // Label preview state (shown when Simulate Printer is ON)
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [labelPreviewHtml, setLabelPreviewHtml] = useState('');


  // Weight mode: Settings > Production is the single source of truth
  const weightMode = defaultWeightMode;
  const isFixedWeightMode = weightMode === 'fixed';
  const isScaleMode = weightMode === 'scale';

  // Weight limits based on unit_weight from product config
  // Min weight = unit_weight, Max weight = unit_weight + tolerance (from config)
  const unitWeight = parseFloat(String(session?.product_unit_weight || session?.unit_weight)) || 0;

  // Sub-unit weight: prefer product's sub_unit_weight, fall back to derived value
  const subUnitWeight = session?.sub_unit_weight && parseFloat(String(session.sub_unit_weight)) > 0
    ? parseFloat(String(session.sub_unit_weight))
    : (unitWeight && session?.item_sub_quantity
        ? unitWeight / parseFloat(String(session.item_sub_quantity))
        : 0);

  // Dynamic target: use box weight for tolerance check only when all sub-units are packed
  const hasSubUnitsEarly = !!session?.has_sub_units;
  const subUnitsIncompleteEarly = hasSubUnitsEarly && subUnitsPacked < (session?.item_sub_quantity || 0);

  // Compute active tare: bag tare during sub-unit packing, case tare for final box, 0 otherwise
  const activeTare = !isScaleMode ? 0
    : subUnitsIncompleteEarly ? (bagTareEnabled ? bagTareWeight : 0)
    : (caseTareEnabled ? caseTareWeight : 0);

  // During sub-unit packing: no tolerance constraint (0 to full case weight)
  // After sub-units done (or no sub-units): enforce unit weight + tolerance
  const minWeight = subUnitsIncompleteEarly ? 0 : (unitWeight > 0 ? unitWeight : 20);
  const maxWeight = subUnitsIncompleteEarly ? unitWeight : (unitWeight > 0 ? unitWeight + weightTolerance : 50);

  // Ref to hold the print trigger handler — updated after usePrintLabel provides handlePrintLabel
  const handlePrintTriggerRef = useRef<((w: number, u: string) => void) | null>(null);

  // Scale weight hook — connects to scale gateway WebSocket for live weight
  const {
    weight: scaleWeight,
    status: scaleStatus,
    isStable: scaleIsStable,
    tare: scaleTare,
    error: scaleError,
    connect: scaleConnect,
    disconnect: scaleDisconnect,
    setWeightRange,
    clearWeightRange,
    enablePrintButton,
    disablePrintButton,
  } = useScaleWeight({
    stationId: station?.station_id,
    autoConnect: isScaleMode,
    onPrintTrigger: (triggerWeight: number, triggerUnit: string) => {
      handlePrintTriggerRef.current?.(triggerWeight, triggerUnit);
    },
  });

  // Sync scale weight → weight state (subtract tare) so existing weightNum/isWeightValid logic works
  useEffect(() => {
    if (isScaleMode && scaleWeight !== undefined) {
      const netWeight = Math.max(0, scaleWeight - activeTare);
      setWeight(netWeight.toFixed(2));
    }
  }, [isScaleMode, scaleWeight, activeTare]);

  // Send PMW ABS range to scale display for Over/Under color feedback
  useEffect(() => {
    if (isScaleMode && scaleStatus === 'connected' && minWeight > 0 && step === 'weight_entry') {
      setWeightRange(minWeight, maxWeight);
    }
    return () => {
      if (isScaleMode) clearWeightRange();
    };
  }, [isScaleMode, scaleStatus, minWeight, maxWeight, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // In fixed mode, auto-set weight to the current phase's target:
  // sub-unit weight while bagging, full unit weight once sub-units are done.
  useEffect(() => {
    if (isFixedWeightMode && step === 'weight_entry') {
      const target = subUnitsIncompleteEarly ? subUnitWeight : unitWeight;
      if (target > 0) setWeight(target.toFixed(2));
    }
  }, [isFixedWeightMode, unitWeight, subUnitWeight, subUnitsIncompleteEarly, step]);

  // If no state, redirect to clock-in
  useEffect(() => {
    if (!station || !session || !employee) {
      navigate('/mobile/clock-in');
    }
  }, [station, session, employee, navigate]);

  // Fetch fresh session data on mount to get updated started_at, status, etc.
  // WCO-2403: fetch by the sub_session_number on screen (not getMySession by employee) —
  // getMySession answers "where am I scheduled as operator", so a packer who is also the
  // scheduled operator of a *different* sub-session would get that other sub-session's
  // data merged into this screen. Same guard pattern as ProductionEntry.
  useEffect(() => {
    if (!session?.sub_session_number || !employee?.employee_number) return;

    const fetchFreshSession = async () => {
      try {
        const result = await operatorService.getSubSessionDetails(session.sub_session_number);
        const freshSession = result.session;
        if (!freshSession) {
          // Sub-session no longer exists — clear stale state and redirect
          clearMobileState();
          navigate('/mobile/clock-in');
          return;
        }
        if (freshSession) {
          // Update session with fresh data (includes started_at, status, paused_duration_seconds, yield data)
          setSession(prev => {
            if (!prev) return prev;
            const updated = {
              ...prev,
              started_at: freshSession.started_at || prev.started_at,
              status: freshSession.status || prev.status,
              paused_duration_seconds: freshSession.paused_duration_seconds ?? prev.paused_duration_seconds,
              total_boxes: (freshSession as any).total_boxes ?? prev.total_boxes,
              all_boxes_count: (freshSession as any).all_boxes_count ?? prev.all_boxes_count,
              // Yield tracking fields for Supervisor Override modal
              actual_output_weight: freshSession.actual_output_weight ?? prev.actual_output_weight,
              waste_weight: freshSession.waste_weight ?? prev.waste_weight,
              yield_percentage: freshSession.yield_percentage ?? prev.yield_percentage,
              expected_yield: freshSession.expected_yield ?? prev.expected_yield,
              initial_material_weight: freshSession.initial_material_weight ?? prev.initial_material_weight,
              target_output_weight: freshSession.target_output_weight ?? prev.target_output_weight,
              raw_material_yield: (freshSession as any).raw_material_yield ?? prev.raw_material_yield,
              raw_material_name: (freshSession as any).raw_material_name ?? prev.raw_material_name,
              raw_material_item_number: (freshSession as any).raw_material_item_number ?? prev.raw_material_item_number,
              raw_material_english: (freshSession as any).raw_material_english ?? (freshSession as any).raw_material_name ?? prev.raw_material_english,
              raw_material_chinese: (freshSession as any).raw_material_chinese ?? prev.raw_material_chinese,
              raw_material_brand: (freshSession as any).raw_material_brand ?? prev.raw_material_brand,
              weight_mode: (freshSession as any).weight_mode ?? prev.weight_mode,
              expected_boxes: (freshSession as any).expected_boxes != null
                ? parseInt(String((freshSession as any).expected_boxes)) || 0
                : prev.expected_boxes,
              bag_offset_from_carryover: (freshSession as any).bag_offset_from_carryover ?? (prev as any).bag_offset_from_carryover,
            } as any;
            return updated;
          });
        }
      } catch (err) {
        console.error('Failed to fetch fresh session data:', err);
      }
    };

    fetchFreshSession();
  }, []); // Run once on mount

  // Persist state to sessionStorage so it survives browser refresh
  useEffect(() => {
    if (session && station && employee) {
      saveMobileState({
        session: session as Record<string, any>,
        station: station as Record<string, any>,
        employee: employee as Record<string, any>,
        boxCount,
        role: 'packer',
        subUnitsPacked,
        subUnitIds,
      });
    }
  }, [session, station, employee, boxCount, subUnitsPacked, subUnitIds]);

  // Show target reached modal when expected boxes is 0 or boxCount meets/exceeds expected
  // Uses derived expectedBoxes (computed after the early return guard below)

  // Refresh box count from backend on mount to ensure accuracy
  useEffect(() => {
    const refreshBoxCount = async () => {
      if (station?.station_barcode) {
        try {
          const result = await operatorService.scanStation(station.station_barcode);
          if (result.success && result.session?.total_boxes !== undefined) {
            // Ensure it's a number (API might return string)
            const backendBoxCount = typeof result.session.total_boxes === 'string'
              ? parseInt(result.session.total_boxes, 10)
              : result.session.total_boxes;
            if (backendBoxCount !== boxCount) {
              setBoxCount(backendBoxCount);
            }
          }
        } catch (err) {
          console.warn('[MobilePackerView] Failed to refresh box count:', err);
        }
      }
    };
    refreshBoxCount();
  }, []); // Only run on mount

  // Restore orphaned sub-units from DB on mount (authoritative state over sessionStorage).
  // subUnitsPacked tracks the highest bag number reached so far. When sub-units exist,
  // that's MAX(sub_unit_number). When none exist, it's the FP-NI carryover bag offset
  // (which the backend has baked into the next sub_unit_number to be assigned).
  useEffect(() => {
    if (!session?.sub_session_number || !session?.has_sub_units) return;
    const restoreSubUnits = async () => {
      try {
        const result = await operatorService.getOrphanedSubUnits(session.sub_session_number);
        const subUnits: Array<{ id: number; sub_unit_number: number }> = result.sub_units || [];
        if (subUnits.length > 0) {
          setSubUnitIds(subUnits.map((su) => su.id));
          setSubUnitsPacked(Math.max(...subUnits.map((su) => Number(su.sub_unit_number) || 0)));
        } else if ((session as any).bag_offset_from_carryover > 0) {
          setSubUnitsPacked((session as any).bag_offset_from_carryover);
        }
      } catch (err) {
        console.warn('[MobilePackerView] Failed to restore sub-units from DB:', err);
      }
    };
    restoreSubUnits();
  }, [session?.sub_session_number, session?.has_sub_units, (session as any)?.bag_offset_from_carryover]);

  // Check for pending box on mount to resume flow after page reload
  useEffect(() => {
    const checkPendingBox = async () => {
      if (!session?.sub_session_number) return;
      // Prevent duplicate checks (React strict mode or state updates can re-trigger)
      if (pendingBoxCheckedRef.current) return;
      pendingBoxCheckedRef.current = true;

      try {
        const result = await operatorService.getPendingBox(session.sub_session_number);
        if (result.success && result.pending_box) {
          // Resume at scan_confirm step with the pending box data
          setWeight(result.pending_box.actual_weight.toString());
          setLastPrintedBarcode(result.pending_box.barcode_number);
          setStep('scan_confirm');
          setScanStep('badge');
          toast.success(t('mobile.resumingPendingBox') || 'Resuming previous box - scan badge to confirm');
        }
      } catch (err) {
        console.warn('[MobilePackerView] Failed to check pending box, using localStorage state:', err);
        // Fall back to localStorage state (already handled by initial state)
      }
    };
    checkPendingBox();
  }, [session?.sub_session_number, t]);

  // Log test info on mount
  useEffect(() => {
    if (station && session && employee) {
    }
  }, [station, session, employee]);

  // If no state, show nothing (useEffect will redirect)
  if (!station || !session || !employee) {
    return null;
  }

  // Parse weight as number
  const weightNum = parseFloat(weight) || 0;
  const isWeightValid = weightNum >= minWeight && weightNum <= maxWeight;

  // In scale mode, Print Label requires scale connected AND weight in tolerance
  const canPrint = isScaleMode
    ? isWeightValid && scaleStatus === 'connected'
    : isWeightValid;

  // WCO-2403: prefer the backend expected_boxes — since WCO-2469, every backend path
  // derives it from the one @tnb/expected-boxes formula (FLOOR(target / unit_weight)),
  // so reading it keeps this screen on the single source of truth instead of
  // re-deriving locally. The local floor stays only as a fallback when the backend
  // value is absent.
  const expectedBoxes = session.expected_boxes != null
    ? Number(session.expected_boxes) || 0
    : (unitWeight > 0 ? Math.floor((session.target_output_weight || 0) / unitWeight) : 0);

  // Show target reached modal when expected boxes is 0 or boxCount meets/exceeds expected
  useEffect(() => {
    if (expectedBoxes === 0 || (expectedBoxes > 0 && boxCount >= expectedBoxes)) {
      const timer = setTimeout(() => setShowTargetReachedModal(true), 500);
      return () => clearTimeout(timer);
    }
  }, [expectedBoxes, boxCount]);

  // Check if sub-session is completed (no more packing allowed)
  const isSessionCompleted = session.status === 'completed';

  // Sub-unit tracking: backend computes has_sub_units (true if discrete units like PC/BG with quantity > 1)
  const hasSubUnits = !!session?.has_sub_units;
  const subUnitsIncomplete = hasSubUnits && subUnitsPacked < session.item_sub_quantity!;

  const { handlePackSubUnit, handlePrintLabel } = usePrintLabel({
    session, setSession, employee, station,
    weightNum, isWeightValid, isSessionCompleted,
    boxCount, subUnitsPacked, setSubUnitsPacked,
    subUnitIds, setSubUnitIds,
    printerMac, printerIp, numberOfCopies, requirePostPrintScan, simulatePrinter,
    labelProductionLocation,
    setBoxCount, setShowTargetReachedModal,
    setIsLoading, setStep, setScanStep, setScannedBadge,
    setLastPrintedBarcode, setShowLabelPreview, setLabelPreviewHtml,
    setShowSubUnitsCompleteModal,
    setWeight,
  });

  // Wire scale print button → handlePrintLabel with guards and debounce.
  // Suppressed during sub-unit packing; the physical PRINT button only works
  // for the final case, matching the "Print Label" UI button's gating.
  useEffect(() => {
    handlePrintTriggerRef.current = (_triggerWeight: number, _triggerUnit: string) => {
      if (step !== 'weight_entry' || isLoading || !isWeightValid || isSessionCompleted) return;
      if (subUnitsIncomplete) return;
      const now = Date.now();
      if (now - lastPrintTriggerRef.current < 2000) return;
      lastPrintTriggerRef.current = now;
      handlePrintLabel();
    };
  }, [step, isLoading, isWeightValid, isSessionCompleted, subUnitsIncomplete, handlePrintLabel]);

  // Enable/disable scale print button subscription based on mode and step
  useEffect(() => {
    if (isScaleMode && step === 'weight_entry') {
      enablePrintButton();
      return () => { disablePrintButton(); };
    }
  }, [isScaleMode, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const { handleManualScanSubmit, handleConfirmScan, handleWeightEntryScan } = usePackerScanning({
    session, employee, station,
    step, scanStep, scannedBadge,
    stationScannedForMenu, setStationScannedForMenu,
    showBadgeMenu, setShowBadgeMenu, isClockingOut,
    showSupervisorOverride: showSupervisorOverride || showSupervisorBadgePrompt,
    badgeAcceptedAtRef, manualInput, setManualInput,
    setStep, setScanStep, setScannedBadge, setWeight,
    setBoxCount,
    setSubUnitsPacked, setSubUnitIds,
    setShowTargetReachedModal, setIsLoading,
  });


  // Handle clock out
  const handleClockOut = async () => {
    setIsClockingOut(true);
    // Mark that this device initiated the clock-out (to ignore the WebSocket event)
    isLocalClockOutRef.current = true;
    try {
      const result = await operatorService.clockOut(
        `@+${employee.employee_number}`,
        station.station_id
      );

      if (result.success) {
        clearMobileState();
        toast.success(t('mobile.clockedOut') || 'Clocked out successfully');
        navigate('/mobile/clock-in');
      } else {
        toast.error(result.message || 'Failed to clock out');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to clock out');
      isLocalClockOutRef.current = false; // Reset flag on error
    } finally {
      setIsClockingOut(false);
    }
  };

  // Cancel scan confirmation - void the pending box and clean up orphaned sub-units
  const handleCancelConfirm = async () => {
    // Void the pending box if we have a barcode
    if (lastPrintedBarcode) {
      try {
        await operatorService.voidBoxByBarcode(
          lastPrintedBarcode,
          'Label voided by operator',
          employee?.user_id || (employee?.employee_number ? `emp_${employee.employee_number}` : undefined)
        );
        toast.success('Label voided');
      } catch (err: any) {
        console.warn('[MobilePackerView] Failed to void box:', err.message);
      }
      setLastPrintedBarcode(null);
    }

    // Delete orphaned sub-units from DB
    if (subUnitIds.length > 0) {
      await Promise.allSettled(subUnitIds.map(id => operatorService.deleteSubUnit(id)));
    }

    setStep('weight_entry');
    setScanStep('badge');
    setScannedBadge(null);
    setManualInput('');
    setSubUnitsPacked(0);
    setSubUnitIds([]);
  };

  // Handle take break - pause the session so the timer stops immediately.
  const handleTakeBreak = async () => {
    if (!session) return;

    setIsTakingBreak(true);
    try {
      await operatorService.pauseSession(
        session.sub_session_number,
        `Break taken by ${employee?.display_name || employee?.employee_number}`,
        true, // isSubSession
        employee?.employee_number,
        'packer'
      );
      clearMobileState();
      toast.success(t('mobile.breakStarted') || 'Break started - session paused');
      setShowBadgeMenu(false);
      navigate('/mobile/clock-in');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start break');
    } finally {
      setIsTakingBreak(false);
    }
  };

  // Handle supervisor badge verification for override
  const handleSupervisorBadgeScan = async (badgeCode: string) => {
    if (!badgeCode.trim()) return;

    setIsVerifyingSupervisor(true);
    try {
      // Extract employee number from badge code (handles @+, @i, @ prefixes)
      const employeeNumber = extractEmployeeNumber(badgeCode.trim());

      // The backend expects the badge in @+XXXX format for verification
      const normalizedBadge = `@+${employeeNumber}`;

      // Verify it's a supervisor badge
      const result = await authService.verifySupervisorBadge(normalizedBadge);

      if (result.valid && result.userId && result.fullName) {
        setOverrideSupervisor({ id: result.userId, name: result.fullName, employee_number: employeeNumber });
        setShowSupervisorBadgePrompt(false);
        setSupervisorBadgeInput('');
        setShowSupervisorOverride(true);
        toast.success(t('mobile.supervisorOverrideActivated') || 'Supervisor override activated');
      } else {
        toast.error(t('mobile.notASupervisor') || 'Badge does not belong to a supervisor');
      }
    } catch (err: any) {
      console.error('Supervisor badge verification failed:', err);
      toast.error(err.response?.data?.message || t('mobile.badgeVerificationFailed') || 'Badge verification failed');
    } finally {
      setIsVerifyingSupervisor(false);
    }
  };

  // Handle weight mode change from supervisor override modal
  const handleManualWeightToggle = (mode: 'fixed' | 'catch' | 'scale') => {
    // Update the session weight mode (toast is shown in SupervisorOverrideModal)
    setSession(prev => {
      return prev ? { ...prev, weight_mode: mode } : prev;
    });
  };

  // When embedded in Layout, set the dynamic page title in the Layout header
  const dynamicTitle = embedded
    ? (t('mobile.packerView') || 'Packer View')
    : '';
  usePageTitle(dynamicTitle);

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} flex flex-col bg-gray-100 dark:bg-gray-900`}>
      {/* Amber Header with Hamburger Menu and Session Time — hidden when embedded in Layout */}
      {!embedded && (
        <header className="bg-amber-600 dark:bg-amber-700 text-white px-4 md:px-6 lg:px-8 py-3 sticky top-0 z-10 flex items-center gap-3">
          <button
            onClick={() => setShowSideMenu(true)}
            className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold">{t('mobile.packerView') || 'Packer View'}</h1>
            <p className="text-sm opacity-80 truncate">{station.station_name} · {session.sub_session_number}</p>
          </div>
          {/* Session Time Display - show when session has started */}
          {session.started_at && (
            <ElapsedTimeDisplay
              startedAt={session.started_at}
              isPaused={sessionStatus === 'paused' || session.status === 'paused'}
              pausedDurationSeconds={session.paused_duration_seconds || 0}
            />
          )}
        </header>
      )}

      <div className="flex-1 flex flex-col">
        {/* Top Bar - Employee Info & Box Count */}
        <div className={`${embedded ? 'px-4 py-2.5' : 'px-4 md:px-6 lg:px-8 py-2.5 bg-amber-50 dark:bg-amber-900/20'} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            {/* Desktop: actions button on the left */}
            {embedded && (
              <button
                onClick={() => setShowActionsDropdown(true)}
                className="flex items-center gap-2 px-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5" />
                Actions
              </button>
            )}
            {/* Employee info — left on mobile only (desktop shows it in Progress card) */}
            {!embedded && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {employee.display_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{employee.display_name}</p>
                  <p className="text-xs text-gray-500">#{employee.employee_number}</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Desktop: session timer on the right */}
            {embedded && session.started_at && (
              <ElapsedTimeDisplay
                startedAt={session.started_at}
                isPaused={sessionStatus === 'paused' || session.status === 'paused'}
                pausedDurationSeconds={session.paused_duration_seconds || 0}
              />
            )}
            {/* Mobile-only scan out icon button */}
            {!embedded && (
              <button
                onClick={() => {
                  setScanOutStep('station');
                  setShowScanOutModal(true);
                }}
                disabled={isClockingOut}
                className="p-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg"
              >
                {isClockingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile: Packing Instructions Section - Collapsible (hidden when embedded, replaced by card grid) */}
        {!embedded && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setIsPackingInfoExpanded(!isPackingInfoExpanded)}
            className="w-full px-4 md:px-6 py-3 flex items-center justify-between md:cursor-default"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Package className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-left">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('mobile.packingInstructions') || 'Packing Instructions'}
                </h3>
                {!isPackingInfoExpanded && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate md:hidden">
                    {session.product_sku} · {session.packing || '-'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 ml-2 md:hidden">
              {isPackingInfoExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </button>
          {/* Mobile: collapsible 2-column grid */}
          <div className={`px-4 pb-3 space-y-2 text-sm md:hidden ${isPackingInfoExpanded ? 'block' : 'hidden'}`}>
              {/* Finished Product section */}
              <div className="text-xs font-semibold uppercase text-amber-600 dark:text-amber-400">Finished Product:</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <div className="text-gray-500">{t('mobile.itemNumber') || 'Item #'}:</div>
                <div className="text-gray-900 dark:text-white font-medium">{session.product_sku}</div>

                <div className="text-gray-500">{t('mobile.englishName') || 'English'}:</div>
                <div className="text-gray-900 dark:text-white font-medium truncate">{session.product_name}{session.product_chinese ? ` (${session.product_chinese})` : ''}</div>

                {session.packing && (
                  <>
                    <div className="text-gray-500">{t('mobile.packingSpec') || 'Packing'}:</div>
                    <div className="text-gray-900 dark:text-white font-medium">{session.packing}</div>
                  </>
                )}

                {expectedBoxes > 0 && (
                  <>
                    <div className="text-gray-500">{t('mobile.expectedBoxes') || 'Expected Boxes'}:</div>
                    <div className="text-amber-600 dark:text-amber-400 font-bold">{expectedBoxes}</div>
                  </>
                )}
              </div>

              {/* Raw Material section */}
              <div className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400 mt-2">Raw Material:</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <div className="text-gray-500">{t('mobile.itemNumber') || 'Item #'}:</div>
                <div className="text-gray-900 dark:text-white font-medium">{session.raw_material_item_number || session.lot_number}</div>

                {(session.raw_material_english || session.raw_material_name) && (
                  <>
                    <div className="text-gray-500">{t('mobile.englishName') || 'English'}:</div>
                    <div className="text-gray-900 dark:text-white font-medium truncate">{session.raw_material_english || session.raw_material_name}{session.raw_material_chinese ? ` (${session.raw_material_chinese})` : ''}</div>
                  </>
                )}

                {session.raw_material_brand && (
                  <>
                    <div className="text-gray-500">Brand:</div>
                    <div className="text-gray-900 dark:text-white font-medium">{session.raw_material_brand}</div>
                  </>
                )}

                <div className="text-gray-500">Lot:</div>
                <div className="text-gray-900 dark:text-white font-medium">{session.lot_number ? `WT ${session.lot_number}` : '-'}</div>

                {unitWeight > 0 && (
                  <>
                    <div className="text-gray-500">Unit Weight:</div>
                    <div className="text-gray-900 dark:text-white font-medium">{unitWeight.toFixed(2)} lbs/box</div>
                  </>
                )}

                {session.initial_material_weight && session.initial_material_weight > 0 && (
                  <>
                    <div className="text-gray-500">Initial Weight:</div>
                    <div className="text-gray-900 dark:text-white font-medium">{Number(session.initial_material_weight || 0).toFixed(2)} lbs</div>
                  </>
                )}

                {apiYield !== null && apiYield > 0 && (
                  <>
                    <div className="text-gray-500">Yield:</div>
                    <div className="text-gray-900 dark:text-white font-medium">{formatYield(apiYield)}</div>
                  </>
                )}
              </div>
          </div>
          {/* Desktop: always-visible 2-line layout */}
          <div className="hidden md:block md:px-6 pb-3 text-sm space-y-1">
              {/* Line 1: Finished product info */}
              <div className="flex items-center gap-x-3 flex-wrap">
                <span className="text-xs font-semibold uppercase text-amber-600 dark:text-amber-400">Finished Product:</span>
                <span className="font-bold text-gray-900 dark:text-white">{session.product_sku}</span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span className="text-gray-700 dark:text-gray-300">{session.product_name}{session.product_chinese ? ` (${session.product_chinese})` : ''}</span>
                {session.packing && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="font-medium text-gray-900 dark:text-white">{session.packing}</span>
                  </>
                )}
                {expectedBoxes > 0 && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="font-bold text-amber-600 dark:text-amber-400">{expectedBoxes} {t('mobile.boxes') || 'boxes'}</span>
                  </>
                )}
              </div>
              {/* Line 2: Raw material info */}
              <div className="flex items-center gap-x-3 flex-wrap text-gray-500 dark:text-gray-400">
                <span className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400">Raw Material:</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{session.raw_material_item_number || session.lot_number}</span>
                {(session.raw_material_english || session.raw_material_name) && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{session.raw_material_english || session.raw_material_name}{session.raw_material_chinese ? ` (${session.raw_material_chinese})` : ''}</span>
                  </>
                )}
                {session.raw_material_brand && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span>Brand: <span className="font-medium text-gray-700 dark:text-gray-300">{session.raw_material_brand}</span></span>
                  </>
                )}
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span>Lot: <span className="font-medium text-gray-700 dark:text-gray-300">{session.lot_number ? `WT ${session.lot_number}` : '-'}</span></span>
                {unitWeight > 0 && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span>{unitWeight.toFixed(2)} lbs/box</span>
                  </>
                )}
                {session.initial_material_weight && session.initial_material_weight > 0 && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span>{Number(session.initial_material_weight || 0).toFixed(2)} lbs initial</span>
                  </>
                )}
                {apiYield !== null && apiYield > 0 && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <span>Yield: <span className="font-medium text-gray-700 dark:text-gray-300">{formatYield(apiYield)}</span></span>
                  </>
                )}
              </div>
          </div>
        </div>
        )}

        {/* Main Content */}
        <div className={`flex-1 flex flex-col p-4 md:mx-auto md:w-full ${embedded ? '' : 'md:max-w-3xl lg:max-w-4xl'}`}>
          {/* Step: Weight Entry */}
          {step === 'weight_entry' && (
            <PackerWeightEntry
              embedded={embedded}
              session={session}
              employee={employee}
              station={station}
              boxCount={boxCount}
              expectedBoxes={expectedBoxes}
              weight={weight}
              setWeight={setWeight}
              weightNum={weightNum}
              isWeightValid={isWeightValid}
              isFixedWeightMode={isFixedWeightMode}
              isScaleMode={isScaleMode}
              scaleWeight={scaleWeight}
              scaleStatus={scaleStatus}
              scaleIsStable={scaleIsStable}
              scaleTare={scaleTare}
              scaleError={scaleError}
              canPrint={canPrint}
              unitWeight={unitWeight}
              minWeight={minWeight}
              maxWeight={maxWeight}
              hasSubUnits={hasSubUnits}
              subUnitsPacked={subUnitsPacked}
              subUnitsIncomplete={subUnitsIncomplete}
              isLoading={isLoading}
              apiYield={apiYield}
              handlePackSubUnit={handlePackSubUnit}
              handlePrintLabel={handlePrintLabel}
              setShowTargetReachedModal={setShowTargetReachedModal}
              activeTare={activeTare}
              grossWeight={scaleWeight}
            />
          )}

          {/* Step: Scan Confirmation */}
          {step === 'scan_confirm' && (
            <PackerScanConfirmation
              scanStep={scanStep}
              scannedBadge={scannedBadge}
              isLoading={isLoading}
              manualInput={manualInput}
              setManualInput={setManualInput}
              handleManualScanSubmit={handleManualScanSubmit}
              handleCancelConfirm={handleCancelConfirm}
              badgeScanInputRef={badgeScanInputRef}
              labelScanInputRef={labelScanInputRef}
              setShowCamera={setShowCamera}
            />
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-24 h-24 mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('mobile.boxConfirmed') || 'Box Confirmed!'}
              </h2>
              <p className="text-gray-500">
                {t('mobile.boxNumber') || 'Box'} #{boxCount}
              </p>
              <div className="mt-6">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-green-600" />
              </div>
            </div>
          )}
        </div>
      </div>

      <PackerModals
        embedded={embedded}
        session={session}
        employee={employee}
        station={station}
        step={step}
        boxCount={boxCount}
        expectedBoxes={expectedBoxes}
        isSessionCompleted={isSessionCompleted}
        isLoading={isLoading}
        isClockingOut={isClockingOut}
        isTakingBreak={isTakingBreak}
        showScanOutModal={showScanOutModal}
        setShowScanOutModal={setShowScanOutModal}
        scanOutStep={scanOutStep}
        setScanOutStep={setScanOutStep}
        pendingScanOutAction={pendingScanOutAction}
        setPendingScanOutAction={setPendingScanOutAction}
        showBadgeMenu={showBadgeMenu}
        setShowBadgeMenu={setShowBadgeMenu}
        showSubUnitsCompleteModal={showSubUnitsCompleteModal}
        setShowSubUnitsCompleteModal={setShowSubUnitsCompleteModal}
        showTargetReachedModal={showTargetReachedModal}
        setShowTargetReachedModal={setShowTargetReachedModal}
        showSideMenu={showSideMenu}
        setShowSideMenu={setShowSideMenu}
        showSupervisorBadgePrompt={showSupervisorBadgePrompt}
        setShowSupervisorBadgePrompt={setShowSupervisorBadgePrompt}
        supervisorBadgeInput={supervisorBadgeInput}
        setSupervisorBadgeInput={setSupervisorBadgeInput}
        isVerifyingSupervisor={isVerifyingSupervisor}
        setIsVerifyingSupervisor={setIsVerifyingSupervisor}
        showSupervisorOverride={showSupervisorOverride}
        setShowSupervisorOverride={setShowSupervisorOverride}
        overrideSupervisor={overrideSupervisor}
        setOverrideSupervisor={setOverrideSupervisor}
        showActionsDropdown={showActionsDropdown}
        setShowActionsDropdown={setShowActionsDropdown}
        showMaterialIssueModal={showMaterialIssueModal}
        setShowMaterialIssueModal={setShowMaterialIssueModal}
        showLabelPreview={showLabelPreview}
        setShowLabelPreview={setShowLabelPreview}
        labelPreviewHtml={labelPreviewHtml}
        numberOfCopies={numberOfCopies}
        showCamera={showCamera}
        setShowCamera={setShowCamera}
        handleClockOut={handleClockOut}
        handleTakeBreak={handleTakeBreak}
        handleCancelConfirm={handleCancelConfirm}
        handleConfirmScan={handleConfirmScan}
        handleWeightEntryScan={handleWeightEntryScan}
        handleSupervisorBadgeScan={handleSupervisorBadgeScan}
        handleManualWeightToggle={handleManualWeightToggle}
        setSession={setSession}
        apiYield={apiYield}
      />
    </div>
  );
};

export default MobilePackerView;
